import { useEffect, useRef, useState } from 'react';
import { Terminal as TerminalIcon } from 'lucide-react';

interface TerminalProps {
  history?: string[];
  onCommand?: (command: string) => void;
  readOnly?: boolean;
  height?: string;
}

export function Terminal({
  history = [],
  onCommand,
  readOnly = false,
  height = '300px',
}: TerminalProps) {
  const [currentCommand, setCurrentCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-scroll to bottom when history changes
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history, commandHistory]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCommand.trim()) return;

    setCommandHistory([...commandHistory, currentCommand]);
    onCommand?.(currentCommand);
    setCurrentCommand('');
    setHistoryIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex =
          historyIndex === -1
            ? commandHistory.length - 1
            : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setCurrentCommand(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setCurrentCommand('');
        } else {
          setHistoryIndex(newIndex);
          setCurrentCommand(commandHistory[newIndex]);
        }
      }
    }
  };

  return (
    <div
      className="rounded-lg border border-dark-200 bg-black shadow-[0_0_20px_rgba(34,197,94,0.1)] overflow-hidden"
      style={{ height }}
    >
      {/* Terminal Header */}
      <div className="bg-dark-50 border-b border-dark-200 px-4 py-2 flex items-center gap-2">
        <TerminalIcon size={16} className="text-primary-500" />
        <span className="text-sm font-semibold text-primary-400">
          Terminal
        </span>
        <div className="flex gap-1.5 ml-auto">
          <div className="w-3 h-3 rounded-full bg-error-600" />
          <div className="w-3 h-3 rounded-full bg-warning-600" />
          <div className="w-3 h-3 rounded-full bg-success-600" />
        </div>
      </div>

      {/* Terminal Content */}
      <div
        ref={terminalRef}
        className="p-4 overflow-y-auto font-mono text-sm"
        style={{ height: 'calc(100% - 45px)' }}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Command History */}
        {history.map((line, index) => (
          <div key={index} className="mb-2">
            {line.startsWith('$') ? (
              <div className="text-primary-500">{line}</div>
            ) : line.startsWith('Error:') || line.startsWith('✗') ? (
              <div className="text-error-400">{line}</div>
            ) : line.startsWith('✓') || line.startsWith('Success:') ? (
              <div className="text-success-400">{line}</div>
            ) : (
              <div className="text-primary-200">{line}</div>
            )}
          </div>
        ))}

        {/* Command Input */}
        {!readOnly && (
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <span className="text-primary-500">$</span>
            <input
              ref={inputRef}
              type="text"
              value={currentCommand}
              onChange={(e) => setCurrentCommand(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-primary-200 outline-none border-none font-mono"
              placeholder="Type a command..."
              autoFocus
            />
          </form>
        )}

        {readOnly && commandHistory.length === 0 && history.length === 0 && (
          <div className="text-primary-200/50 text-center py-8">
            Terminal output will appear here
          </div>
        )}
      </div>
    </div>
  );
}
